import { ProductSuggestion } from 'components/InquireWorkOrderProductLevelErrorRow';
import { Entry } from 'types';
import { isEuCustomsDeclaration } from '@xbcb/work-order-utils/dist/isEuCustomsDeclaration';
import { Product, CustomsDeclaration } from '@xbcb/api-gateway-client';
import { Maybe } from '../../__generated__/graphql';

export const getProductIdentifiersFromEntry = (
  entry: Entry,
): ProductSuggestion[] =>
  isEuCustomsDeclaration(entry)
    ? getProductIdentifiersFromCustomsDeclaration(entry as CustomsDeclaration)
    : getProductIdentifiersFromCustomsEntry(entry);

export { brexitProductInfoFieldsCustomsDeclarationFragment } from './getProductIdentifiersFromCustomsDeclaration.query';

/**
 * Utility type used to store an alternate external identifier reference with a product
 *  Used as patch for a brexit issue, CR-116668482
 */
type ProductAndAlternateIdentifierVersion = {
  product: Maybe<Product> | undefined;
  alternateIdentifierVersion?: Maybe<number>;
};

const getProductIdentifiersFromCustomsEntry = (entry: Entry) => {
  const products =
    entry.invoices
      ?.map((invoice) => invoice.products)
      .flat()
      .map((product) => ({
        product: product?.product,
        alternateIdentifierVersion:
          // Always use the first line since externalReference is same for all lines
          product?.lines?.[0]?.externalReference?.skuComplianceVersion,
      }))
      .filter(Boolean) ?? [];

  return productsToProductSuggestions(products);
};

const getProductIdentifiersFromCustomsDeclaration = (
  cuDe: CustomsDeclaration,
) => {
  const goodsShipments = cuDe.goodsShipment;
  const goodsItems = goodsShipments?.map(({ goodsItem }) => goodsItem).flat();
  const commodities = goodsItems?.map((goodsItem) => goodsItem?.commodity);
  const products = commodities
    ?.map((commodity) => commodity?.product)
    .filter((product): product is Product => Boolean(product))
    .map((product) => ({ product }));

  return products ? productsToProductSuggestions(products) : [];
};

const productsToProductSuggestions = (
  products: ProductAndAlternateIdentifierVersion[],
): ProductSuggestion[] =>
  (products
    .map(({ product, alternateIdentifierVersion }) => {
      const sku = product?.clientIdentifier;
      if (!sku) return null;
      const skuVersion =
        // Fallback to clientIdentifierVersion in product record due to missing lines issue (fixed in CR-116668482)
        alternateIdentifierVersion || product?.clientIdentifierVersion;
      const asin = product?.secondaryIdentifiers?.find(
        (identifier) => identifier.type === 'ASIN',
      );
      return {
        asin: asin?.value,
        sku,
        skuVersion,
      };
    })
    .filter(Boolean) as ProductSuggestion[]) ?? [];
